import ContractAttachment from '@/components/ContractAttachment'
import TextEditorCustomize from '@/components/TextEditorCustomize'
import ProblemIcons from '@/icons/ProblemIcons'
import useAuthStore from '@/stores/useAuthStore'
import {
    formatDateWithFormatter,
    randomAvatarSrc,
    uniqBy,
} from '@/utils/function'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar, Typogaphy } from 'vnetwork-ui'
import { ContractProcess } from '../../types'
import ProcessHitories from './Histories'
import ProcessFeedbackForm from './ProcessFeedbackForm'

interface IContractProcessDetailProps {
    shouldCheckRegistrationDate?: boolean
    process: ContractProcess
    contractID?: number
}

const DetailComponent: React.FC<IContractProcessDetailProps> = ({
    process,
    shouldCheckRegistrationDate = false,
}) => {
    const { employee } = useAuthStore()
    const { t } = useTranslation()

    const isArchived = useMemo(
        () =>
            ['cancelled', 'rejected'].includes(
                process?.contract_process_status
            ) || process?.archived,
        [process]
    )

    const isActiveProcess = useMemo(
        () =>
            !isArchived &&
            ['in-progress', 'pending'].includes(
                process?.contract_process_status
            ),
        [isArchived, process]
    )

    const isAssignee = useMemo(
        () =>
            process?.contract_process_assignees?.includes(
                employee?.employee_id as number
            ),
        [process, employee]
    )

    const isCreator = useMemo(
        () => employee?.employee_id === process?.contract_process_employee_id,
        [process, employee]
    )

    const canProvideFeedback = isActiveProcess && (isAssignee || isCreator)

    return (
        <div className="w-full flex flex-col">
            {/* Pinned info bar */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mb-3">
                {process?.contract_process_deadline && (
                    <div>
                        <span className="text-gray-400">
                            {t('contract_process_deadline')}:{' '}
                        </span>
                        <span className="text-orange-600 font-medium">
                            {formatDateWithFormatter(
                                process.contract_process_deadline,
                                'DD/MM/YYYY'
                            )}
                        </span>
                    </div>
                )}
                {process?.employee_names?.length > 0 && (
                    <div>
                        <span className="text-gray-400">
                            {t('contract_process_assignees')}:{' '}
                        </span>
                        <span className="font-medium">
                            {process.employee_names.join(', ')}
                        </span>
                    </div>
                )}
            </div>

            {/* Chat conversation area */}
            <div className="space-y-3">
                {/* First message: creator's request */}
                <div className={`max-w-[90%] ${isCreator ? 'ml-auto' : ''}`}>
                    {/* Name outside bubble */}
                    <div
                        className={`mb-1 ${isCreator ? 'text-right pr-10' : 'pl-10'}`}
                    >
                        <Typogaphy className="text-xs font-medium text-gray-600">
                            {process?.employee_name || '---'}
                        </Typogaphy>
                    </div>

                    {/* Avatar + Bubble row */}
                    <div
                        className={`flex items-start gap-2 ${isCreator ? 'flex-row-reverse' : ''}`}
                    >
                        <Avatar
                            size="small"
                            src={randomAvatarSrc(process?.employee_name)}
                            className="shrink-0"
                        />
                        <div
                            className={`min-w-0 ${
                                isCreator
                                    ? 'rounded-2xl rounded-tr-none bg-blue-100/80 px-3 py-2.5'
                                    : 'rounded-2xl rounded-tl-none bg-gray-100 px-3 py-2.5'
                            }`}
                        >
                            {/* Problem banner */}
                            {!!process?.contract_process_problem && (
                                <div className="mb-2 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded text-xs">
                                    <div className="flex items-center gap-1 text-amber-700 font-medium mb-0.5">
                                        <ProblemIcons className="w-3 h-3" />
                                        {t('contract_process_problem')}
                                    </div>
                                    <p className="text-amber-800">
                                        {process.contract_process_problem}
                                    </p>
                                </div>
                            )}

                            {/* Content */}
                            <TextEditorCustomize
                                readOnly={true}
                                value={{
                                    text:
                                        process?.contract_process_content || '',
                                    attachments: process?.attachments || [],
                                }}
                            />

                            {/* Attachments */}
                            {process?.attachments &&
                                process.attachments.length > 0 && (
                                    <div className="mt-2">
                                        <ContractAttachment
                                            attachments={uniqBy(
                                                process.attachments,
                                                'attachment_id'
                                            )}
                                            hideLabel
                                        />
                                    </div>
                                )}
                        </div>
                    </div>

                    {/* Timestamp outside bubble */}
                    <Typogaphy
                        className={`text-[11px] text-gray-400 mt-1 ${isCreator ? 'text-right pr-10' : 'pl-10'}`}
                    >
                        {formatDateWithFormatter(process?.created_at, 'HH:mm', {
                            fromUTC: true,
                        })}
                    </Typogaphy>
                </div>

                {/* History messages (chat thread) */}
                <ProcessHitories
                    contractProcessID={process?.contract_process_id}
                    contractID={process?.contract_processable_id}
                    shouldCheckRegistrationDate={shouldCheckRegistrationDate}
                />
            </div>

            {/* Feedback form */}
            {canProvideFeedback && (
                <ProcessFeedbackForm
                    contractID={process?.contract_processable_id}
                    contractProcessID={process?.contract_process_id}
                    hideStatus={!isAssignee}
                />
            )}
        </div>
    )
}

const Detail = (props: IContractProcessDetailProps) => {
    return <DetailComponent {...props} />
}
export default Detail
